import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import ToolTip from "components/design/toolTip";
import Card from "components/DesignSystem/Card/Card";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import ThreeDots from "static/images/ThreeDots.svg";
import QuestionIconTP from "static/images/QuestionIconTP.svg";
import { Info } from "components/icons/Info";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { formatAmount } from "utils/foramtAmount";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useSendQuarterToChatMutation } from "store/apis/estimatedTax";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { setEstimatedTaxCardInChat } from "store/slices/estimatedTaxCardInChat";
import { IRSPaymentTracker } from "./IRSPaymentTracker";
import { HowToPayModal } from "./HowToPayModal";
import {
  QuarterlyBreakdown,
  TaxWaterfall,
  IRSPayment,
  EstimatedTaxSettings,
} from "types/Models/estimatedTax";

const STATUS_TAG: Record<string, TagColorType> = {
  PAID: "green",
  DUE: "orange",
  OVERDUE: "red",
  UPCOMING: "gray",
  ROLLED_FORWARD: "purple",
};

const STATUS_LABEL: Record<string, string> = {
  PAID: "Paid",
  DUE: "Due Soon",
  OVERDUE: "Overdue",
  UPCOMING: "Upcoming",
  ROLLED_FORWARD: "Rolled Forward",
};

type Props = {
  waterfall: TaxWaterfall;
  quarters: QuarterlyBreakdown[];
  payments: IRSPayment[];
  entityId: string;
  year: string;
  settings?: EstimatedTaxSettings | null;
};

const PriorYearCreditTag = ({
  creditApplied,
  remaining,
  overpaymentYear,
}: {
  creditApplied: number;
  remaining: number;
  overpaymentYear: number | null;
}) => {
  const label = overpaymentYear
    ? `${overpaymentYear} overpayment`
    : "Prior year overpayment";
  const description = `${label} credit of ${formatAmount(
    creditApplied
  )} applied. Remaining prior credit balance: ${formatAmount(remaining)}.`;
  return (
    <ToolTip text={description} side="top">
      <span className="t-flex t-cursor-default t-items-center">
        <Tag tagType="gray" icon={false} rounded size="small">
          <span className="t-flex t-items-center t-gap-1 t-text-[11px]">
            Prior credit applied
            <Info size="12" />
          </span>
        </Tag>
      </span>
    </ToolTip>
  );
};

export const EstimatedTaxOverview = ({
  waterfall,
  quarters,
  payments,
  entityId,
  year,
  settings,
}: Props) => {
  const howToPayModal = useModal();
  const dispatch = useDispatch();
  const { isAdmin, isCpa } = useRoleBasedView();
  const isServiceUser = isAdmin || isCpa;
  const { alertToast } = useToast();
  const [sendQuarterToChat] = useSendQuarterToChatMutation();
  const [createMessageDraft] = useCreateMessageDraftMutation();
  const [sendingQuarter, setSendingQuarter] = useState<number | null>(null);

  const renderQuarterAmount = (q: QuarterlyBreakdown) => {
    const hasOverpayment = q.overpayment_applied > 0;
    const hasRolledForward =
      q.rolled_forward_amount != null && q.rolled_forward_amount > 0;

    if (q.status === "ROLLED_FORWARD") {
      let rolledForwardTooltip: string | null = null;
      if (hasOverpayment && hasRolledForward) {
        rolledForwardTooltip = `${formatAmount(
          q.overpayment_applied
        )} overpayment was applied; ${formatAmount(
          q.rolled_forward_amount!
        )} rolled forward to next quarter`;
      } else if (hasOverpayment) {
        rolledForwardTooltip = `${formatAmount(
          q.overpayment_applied
        )} overpayment was applied; remaining balance rolled to next quarter`;
      } else if (hasRolledForward) {
        rolledForwardTooltip = `${formatAmount(
          q.rolled_forward_amount!
        )} rolled forward to next quarter`;
      }

      return (
        <div className="t-mt-1">
          <div className="t-text-body-sm t-text-text-30 t-mb-0.5">Paid</div>
          <div className="t-flex t-items-center t-gap-1">
            <AmountSuperScript amount={q.paid_amount} />
            {rolledForwardTooltip && (
              <ToolTip text={rolledForwardTooltip} side="top">
                <span className="t-flex t-text-text-30 t-cursor-default">
                  <Info />
                </span>
              </ToolTip>
            )}
          </div>
        </div>
      );
    }

    if (q.status === "PAID") {
      return (
        <div className="t-mt-1">
          <div className="t-text-body-sm t-text-text-30 t-mb-0.5">Paid</div>
          <div className="t-flex t-items-center t-gap-1">
            <AmountSuperScript amount={q.paid_amount} />
            {hasOverpayment && (
              <ToolTip
                text={`Includes ${formatAmount(
                  q.overpayment_applied
                )} overpayment applied from a prior quarter`}
                side="top"
              >
                <span className="t-flex t-text-text-30 t-cursor-default">
                  <Info />
                </span>
              </ToolTip>
            )}
          </div>
        </div>
      );
    }

    let tooltipText: string | null = null;
    if (hasRolledForward) {
      tooltipText = `Includes ${formatAmount(
        q.rolled_forward_amount!
      )} rolled forward from prior quarter`;
    } else if (hasOverpayment) {
      tooltipText = `${formatAmount(
        q.overpayment_applied
      )} overpayment from a prior quarter will be applied`;
    }

    return (
      <div className="t-mt-1">
        <div className="t-text-body-sm t-text-text-30 t-mb-0.5">Estimated</div>
        <div className="t-flex t-items-center t-gap-1">
          <AmountSuperScript amount={q.estimated_payment} />
          {tooltipText && (
            <ToolTip text={tooltipText} side="top">
              <span className="t-flex t-text-text-30 t-cursor-default">
                <Info />
              </span>
            </ToolTip>
          )}
        </div>
      </div>
    );
  };

  const handleSendToChat = async (q: QuarterlyBreakdown) => {
    setSendingQuarter(q.quarter);
    try {
      const { channel_url, card_content } = await sendQuarterToChat({
        entityId,
        quarter: q.quarter,
        year: Number(year),
      }).unwrap();
      await createMessageDraft({
        channelUrl: channel_url,
        message: card_content.message_text,
      });
      dispatch(
        setEstimatedTaxCardInChat({
          entityId,
          quarter: q.quarter,
          year: Number(year),
          label: q.label,
          status: q.status,
          deadline: q.deadline,
          estimated_payment: q.estimated_payment,
          paid_amount: q.paid_amount,
          channel_url,
        })
      );
      dispatch(setToOpenChatId(channel_url));
      dispatch(openFloatingChat());
    } catch {
      alertToast({ message: "Failed to send to chat" });
    } finally {
      setSendingQuarter(null);
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-5">
      {/* Summary strip */}
      <Card.Root>
        <Card.Body>
          <div className="t-flex t-items-stretch t-gap-6">
            <div className="t-flex-1 t-space-y-1">
              <p className="t-text-body-sm t-text-text-30">
                Total Taxable Income
              </p>
              <p className="t-text-subtitle t-font-semibold t-text-text-100">
                <AmountSuperScript amount={waterfall.total_amount} />
              </p>
            </div>
            <Divider orientation="vertical" />
            <div className="t-flex-1 t-space-y-1">
              <p className="t-text-body-sm t-text-text-30">
                Annual Federal Tax
              </p>
              <div className="t-flex t-items-center t-gap-1">
                <p className="t-m-0 t-text-subtitle t-font-semibold t-text-text-100">
                  <AmountSuperScript amount={waterfall.federal_tax} />
                </p>
                {waterfall.prior_year_credit_applied > 0 && (
                  <PriorYearCreditTag
                    creditApplied={waterfall.prior_year_credit_applied}
                    remaining={waterfall.prior_year_overpayment_remaining}
                    overpaymentYear={settings?.overpayment_year ?? null}
                  />
                )}
              </div>
            </div>
            <Divider orientation="vertical" />
            <div className="t-flex-1 t-space-y-1">
              <p className="t-text-body-sm t-text-text-30">Paid YTD</p>
              <p className="t-text-subtitle t-font-semibold t-text-green-70">
                <AmountSuperScript amount={waterfall.ytd_payments} />
              </p>
            </div>
            <Divider orientation="vertical" />
            <div className="t-flex-1 t-space-y-1">
              <p className="t-text-body-sm t-text-text-30">Remaining Due</p>
              <p className="t-text-subtitle t-font-semibold t-text-text-100">
                <AmountSuperScript amount={waterfall.remaining_tax_due} />
              </p>
            </div>
            <Divider orientation="vertical" />
            <div className="t-flex-1 t-space-y-1">
              <p className="t-text-body-sm t-text-text-30">Federal Tax Rate</p>
              <p className="t-text-subtitle t-font-semibold t-text-text-100">
                21%
              </p>
            </div>
          </div>
        </Card.Body>
      </Card.Root>

      {/* Quarterly cards */}
      <Card.Root>
        <Card.Header
          bordered={false}
          className={classNames("t-flex-col t-items-start", {
            "!t-pb-0": waterfall.below_threshold,
          })}
        >
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <Card.Title className="!t-text-body">
              Quarterly Payment Schedule
            </Card.Title>
            <Button
              customType="ghost"
              size="small"
              onClick={howToPayModal.open}
            >
              <div className="t-flex t-items-center t-gap-1.5">
                <img src={QuestionIconTP} alt="How to Pay" />
                How to Pay
              </div>
            </Button>
          </div>
          {waterfall.below_threshold && (
            <p className="t-text-body-sm t-text-text-30 t-w-full t-text-left">
              Your estimated tax is under $500, so no quarterly payments are
              required this year.
            </p>
          )}
        </Card.Header>
        <Card.Body className="!t-mt-3">
          <div className="t-grid t-grid-cols-2 md:t-grid-cols-4 t-gap-4">
            {quarters.map((q) => (
              <div
                key={q.quarter}
                className="t-flex t-flex-col t-gap-2 t-p-4 t-rounded-lg t-border t-border-neutral-10 t-bg-surface-lighter-grey"
              >
                <div className="t-flex t-items-center t-justify-between">
                  <span className="t-text-subtext t-font-semibold t-text-text-100">
                    {q.label}
                  </span>
                  <div className="t-flex t-items-center t-gap-1">
                    {q.is_estimate && !waterfall.below_threshold && (
                      <Tag tagType="gray" icon={false} rounded size="small">
                        Est.
                      </Tag>
                    )}
                    {waterfall.below_threshold ? (
                      <Tag tagType="gray" icon={false} rounded size="small">
                        Not required
                      </Tag>
                    ) : (
                      <Tag
                        tagType={STATUS_TAG[q.status] || "gray"}
                        icon={false}
                        rounded
                        size="small"
                      >
                        {STATUS_LABEL[q.status] || q.status}
                      </Tag>
                    )}
                    {isServiceUser && !waterfall.below_threshold && (
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <Button
                            customType="ghost_icon"
                            size="xsmall"
                            isLoading={sendingQuarter === q.quarter}
                          >
                            <img src={ThreeDots} alt="Options" />
                          </Button>
                        </Dropdown.Trigger>
                        <Dropdown.Portal>
                          <Dropdown.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                          >
                            <Dropdown.Item
                              onSelect={() => handleSendToChat(q)}
                              disabled={sendingQuarter !== null}
                            >
                              Send to Chat
                            </Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown.Portal>
                      </Dropdown.Root>
                    )}
                  </div>
                </div>
                {!waterfall.below_threshold && renderQuarterAmount(q)}
                <div className="t-text-body-sm t-italic t-text-neutral-40">
                  Due {dayjs(q.deadline).format(DISPLAY_DATE)}
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card.Root>

      {/* Payment Tracker */}
      <IRSPaymentTracker
        payments={payments}
        totalFederalTax={waterfall.federal_tax}
        ytdPayments={waterfall.ytd_payments}
        entityId={entityId}
        year={year}
      />

      {howToPayModal.isOpen && <HowToPayModal {...howToPayModal} />}
    </div>
  );
};
